// hooks/useRecentlyViewed.js
// ─────────────────────────────────────────────────────────────────────────────
// Stores & retrieves the last N visited products from localStorage.
// Shape saved per product (minimal — enough to render a card):
// {
//   id, nameEn, nameAr, primaryImageUrl,
//   price, discountPrice,
//   collectionNameEn, categoryNameEn, subCategoryNameEn,
//   savedAt   ← timestamp for sorting / TTL
// }
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY  = "addmix_recently_viewed";
const MAX_ITEMS    = 10;          // max products kept in storage
const TTL_MS       = 7 * 24 * 60 * 60 * 1000;  // 7 days

// ── helpers ───────────────────────────────────────────────────────────────────
const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // drop expired entries
    const now = Date.now();
    return parsed.filter((p) => now - p.savedAt < TTL_MS);
  } catch {
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage full or blocked — fail silently
  }
};

// ── public API ────────────────────────────────────────────────────────────────
export const useRecentlyViewed = () => {
  const [items, setItems] = useState(() => readStorage());

  // keep items fresh (e.g. if another tab updates storage)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /**
   * Call this inside ProductProfile when a product is loaded.
   * Pass the full product object from the API — the hook extracts
   * only what it needs so the stored payload stays tiny.
   */
  const addProduct = useCallback((product) => {
    if (!product?.id) return;

    // build the minimal shape we store
    const entry = {
      id:               product.id,
      nameEn:           product.nameEn,
      nameAr:           product.nameAr,
      primaryImageUrl:  product.images?.[0]?.image_url ?? product.primaryImageUrl ?? null,
      // cheapest active price across variants
      price: (() => {
        const prices = (product.variants ?? [])
          .map((v) => v.discountPrice ?? v.price)
          .filter(Boolean);
        return prices.length ? Math.min(...prices) : null;
      })(),
      originalPrice: (() => {
        const prices = (product.variants ?? [])
          .map((v) => v.price)
          .filter(Boolean);
        return prices.length ? Math.min(...prices) : null;
      })(),
      collectionNameEn: product.collection?.nameEn ?? "",
      categoryNameEn:   product.category?.nameEn   ?? "",
      subCategoryNameEn:product.subCategory?.nameEn ?? "",
      savedAt:          Date.now(),
    };

    setItems((prev) => {
      // remove duplicate (same id) then prepend
      const deduped = prev.filter((p) => p.id !== entry.id);
      const next    = [entry, ...deduped].slice(0, MAX_ITEMS);
      writeStorage(next);
      return next;
    });
  }, []);

  /**
   * Clear all recently viewed (e.g. logout).
   */
  const clearAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setItems([]);
  }, []);

  return { items, addProduct, clearAll };
};